export type EscopoAtalho = 'global' | 'lista' | 'formulario' | 'grade' | 'dialogo';

export interface Atalho {
  id: string;
  teclas: string[];
  escopo: EscopoAtalho;
  descricao: string;
  quando?: () => boolean;
  executar: (e: KeyboardEvent) => void;
}

const registrados = new Map<string, Atalho>();

/** 'Ctrl+Shift+S' ← KeyboardEvent (Cmd conta como Ctrl no macOS). */
export function combinacao(e: Pick<KeyboardEvent, 'key' | 'ctrlKey' | 'metaKey' | 'altKey' | 'shiftKey' | 'code'>): string {
  const partes: string[] = [];
  if (e.ctrlKey || e.metaKey) partes.push('Ctrl');
  if (e.altKey) partes.push('Alt');
  if (e.shiftKey) partes.push('Shift');
  let tecla = e.key;
  if (/^Digit\d$/.test(e.code) && (e.altKey || e.ctrlKey)) tecla = e.code.slice(5);
  else if (tecla.length === 1) tecla = tecla.toUpperCase();
  else if (tecla === 'Esc') tecla = 'Escape';
  if (!['Control', 'Meta', 'Alt', 'Shift'].includes(tecla)) partes.push(tecla === ' ' ? 'Space' : tecla);
  return partes.join('+');
}

const MODIFICADORES = ['Ctrl', 'Alt', 'Shift'];

export function normalizar(teclas: string): string {
  const p = teclas.split('+').map((x) => x.trim()).map((x) => MODIFICADORES.find((m) => m.toLowerCase() === x.toLowerCase()) ?? x);
  const mods = MODIFICADORES.filter((m) => p.includes(m));
  const tecla = p.filter((x) => !MODIFICADORES.includes(x)).map((x) => (x.length === 1 ? x.toUpperCase() : x));
  return [...mods, ...tecla].join('+');
}

export class ConflitoAtalho extends Error {}

/** Registra e devolve a remoção. Mesma combinação duas vezes no mesmo escopo é erro de programação (07 §8). */
export function registrarAtalho(a: Atalho): () => void {
  for (const outro of registrados.values()) {
    if (outro.id === a.id || outro.escopo !== a.escopo) continue;
    const repetida = a.teclas.map(normalizar).find((t) => outro.teclas.map(normalizar).includes(t));
    if (repetida) throw new ConflitoAtalho(`Atalho ${repetida} repetido no escopo ${a.escopo}: ${outro.id} e ${a.id}`);
  }
  registrados.set(a.id, a);
  return () => {
    if (registrados.get(a.id) === a) registrados.delete(a.id);
  };
}

export function listarAtalhos(): Atalho[] {
  return [...registrados.values()];
}

/** Escopo mais específico primeiro: diálogo > grade > formulário > lista > global. */
const PRIORIDADE: EscopoAtalho[] = ['dialogo', 'grade', 'formulario', 'lista', 'global'];

export function despachar(e: KeyboardEvent): boolean {
  // Preenchimento automático do navegador dispara keydown sem `key`.
  if (!e.key) return false;
  const chave = combinacao(e);
  const candidatos = [...registrados.values()]
    .filter((a) => a.teclas.map(normalizar).includes(chave) && (a.quando?.() ?? true))
    .sort((x, y) => PRIORIDADE.indexOf(x.escopo) - PRIORIDADE.indexOf(y.escopo));
  const alvo = candidatos[0];
  if (!alvo) return false;
  e.preventDefault();
  alvo.executar(e);
  return true;
}

let instalado = false;

export function instalarAtalhos() {
  if (instalado) return;
  instalado = true;
  window.addEventListener('keydown', (e) => {
    if (e.defaultPrevented) return;
    despachar(e);
  });
}

export function limparAtalhosParaTeste() {
  registrados.clear();
}
